import { DeleteOutlined, QrcodeOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Form, Input, Radio, Select, Tag, Upload } from "antd";
import React, { useState } from "react";
import { DataCard } from "~/components/dataCards/dataCard.jsx";
import { CategoryStartMethodSelect } from "~/components/form/CategoryStartMethodSelect.jsx";
import { SwitchYesNo } from "~/components/form/SwitchYesNo.jsx";
import { HelpBubble } from "~/components/help/helpBubble.jsx";
import { CopyIcon } from "~/components/icon/copy.jsx";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import {
  normFileDuringFormUpload,
  ruleCheckImageMaxSize,
} from "~/lib/helpers.js";
import { phValidator } from "~/lib/localize.js";
import {
  getDamageSchematicUrl,
  getDefaultDamageSchematicUrl,
} from "../status/damageSchematic.js";

export const CardGeneral = ({ currentMaterial, saveData, readOnly, kist }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [removeSchematic, setRemoveSchematic] = useState(false);
  const schematicUpload = Form.useWatch("damage_schematic_upload", form);
  const enable_damage_schematic = Form.useWatch(
    "enable_damage_schematic",
    form,
  );
  const defaultSchematicUrl = getDefaultDamageSchematicUrl(currentMaterial);
  const schematicUrl = getDamageSchematicUrl(currentMaterial);

  const saveForm = async () => {
    try {
      setLoading(true);
      const { damage_schematic_upload, ...values } =
        await form.validateFields();

      // manipulate values for save
      const payload = {
        ...values,
        remove_damage_schematic: removeSchematic,
        kist: kist && {
          category: values?.kist_category,
          pilots: values?.pilots,
          type: values?.type,
          type_group: values?.type_group,
          callsign: values?.callsign,
          start_methode: values?.start_methode,
          registratie: values?.registratie,
          in_dto: values?.in_dto,
        },
      };
      let res = await saveData(payload);

      // file needs a separate multipart post
      const file = damage_schematic_upload?.[0]?.originFileObj;
      if (res && file) {
        res = await saveData({ damage_schematic: file }, { asForm: true });
        form.setFieldsValue({ damage_schematic_upload: [] });
      }
      if (res) setRemoveSchematic(false);

      setLoading(false);
      return res;
    } catch (info) {
      console.log("Validate Failed:", info);
      setLoading(false);
    }
  };

  const [downloading, setDownloading] = useState(false);
  const downloadCode = async () => {
    if (downloading) return;
    setDownloading(true);
    await api.open_file(
      `materiaal/qrcode?id=${currentMaterial.id}&label=${currentMaterial.name}`,
      `QR ${currentMaterial.name}.png`,
    );
    setDownloading(false);
  };

  return (
    <DataCard
      title={$t("Algemeen")}
      readOnly={readOnly}
      onSave={saveForm}
      loading={loading}
    >
      <Form
        form={form}
        name="general"
        initialValues={{
          ...kist,
          ...currentMaterial,
          kist_category: kist?.category,
        }}
        requiredMark={false}
        labelCol={{ span: 10 }}
        wrapperCol={{ span: 16 }}
      >
        <Form.Item
          label={$t("Naam")}
          name="name"
          rules={[
            {
              required: true,
              message: $t("Maximaal ${c} characters", { c: 200 }),
              max: 200,
            },
          ]}
        >
          <Input placeholder={$t("Naam van materiaal")} disabled={readOnly} />
        </Form.Item>

        {kist && (
          <Form.Item
            label={$t("Registratie")}
            name="registratie"
            rules={[phValidator]}
          >
            <Input placeholder={$t("registratie nummer")} disabled={readOnly} />
          </Form.Item>
        )}

        {kist && (
          <Form.Item
            label={$t("Wedstrijdnummer")}
            name="callsign"
            rules={[
              {
                required: true,
                message: $t("Maximaal ${c} characters", { c: 20 }),
                max: 20,
              },
            ]}
          >
            <Input disabled={readOnly} />
          </Form.Item>
        )}

        <Form.Item
          label={$t("Type")}
          name="type"
          rules={[
            {
              required: true,
              message: $t("Maximaal ${c} characters", { c: 200 }),
              max: 200,
            },
          ]}
        >
          <Input placeholder={$t("Type materiaal")} disabled={readOnly} />
        </Form.Item>

        {kist && (
          <Form.Item
            label={
              <p>
                {$t("Type label")}{" "}
                {kist && (
                  <HelpBubble
                    content={$t(
                      "Onder deze beschrijving zal hij selecteerbaar zijn onder de aanmeldingen en dagelijkse startadministratie.",
                    )}
                  />
                )}
              </p>
            }
            name="type_group"
            rules={[
              {
                required: false,
                message: $t("Maximaal ${c} characters", { c: 200 }),
                max: 200,
              },
            ]}
          >
            <Input placeholder={kist.type} disabled={readOnly} />
          </Form.Item>
        )}

        {!kist && (
          <Form.Item label={$t("Categorie")} name="category">
            <Select disabled={readOnly}>
              <Select.Option key="rollend" value="rollend">
                {$t("Rollend")}
              </Select.Option>
              <Select.Option key="overig" value="overig">
                {$t("Overig")}
              </Select.Option>
            </Select>
          </Form.Item>
        )}

        {kist && (
          <Form.Item label={$t("Zitplaatsen")} name="pilots">
            {readOnly ? (
              <Input disabled />
            ) : (
              <Radio.Group disabled={readOnly} buttonStyle="solid">
                <Radio.Button style={{ width: 30 }} value={1}>
                  1
                </Radio.Button>
                <Radio.Button style={{ width: 30 }} value={2}>
                  2
                </Radio.Button>
              </Radio.Group>
            )}
          </Form.Item>
        )}

        <Form.Item
          label={
            <p>
              {$t("Meldingen")}{" "}
              <HelpBubble
                content={$t(
                  "Hiermee kan je (tijdelijk) het maken van nieuwe meldingen uitschakelen",
                )}
              />
            </p>
          }
          name="enable_meldingen"
        >
          <SwitchYesNo disabled={readOnly} />
        </Form.Item>

        <Form.Item
          label={
            <p>
              {$t("Overschrijf status")}{" "}
              <HelpBubble
                content={$t(
                  "Overschrijf status van materiaal met deze setting, er wordt dan niet gekeken naar de onderliggende meldingen",
                )}
              />
            </p>
          }
          extra={
            <p>
              {$t("Huidige status")}: {$t(currentMaterial.status)}
            </p>
          }
          name="manual_status"
        >
          <Select disabled={readOnly}>
            <Select.Option key="1" value="">
              {$t("Geen, hangt af van de meldingen")}
            </Select.Option>
            <Select.Option key="2" value="in onderhoud">
              <Tag color="warning">{$t("In onderhoud")}</Tag>
            </Select.Option>
            <Select.Option key="3" value="niet inzetbaar">
              <Tag color="error">{$t("Niet inzetbaar")}</Tag>
            </Select.Option>
          </Select>
        </Form.Item>

        {kist && (
          <Form.Item
            label={
              <p>
                {$t("In DTO")}{" "}
                <HelpBubble
                  content={$t(
                    "Kist is geregistreerd als lesvliegtuig in de DTO (Declared Training Organisation). Indien het val, zal als de gezagvoerder een instructeur is de vlucht automatisch als lesvlucht (FIS) neergezet worden",
                  )}
                />
              </p>
            }
            name="in_dto"
          >
            <SwitchYesNo disabled={readOnly} />
          </Form.Item>
        )}

        {kist && <CategoryStartMethodSelect readOnly={readOnly} form={form} />}

        <Form.Item
          label={
            <p>
              {$t("Reserveerbaar")}{" "}
              <HelpBubble
                content={$t("Kan door leden gereserveerd worden in de app")}
              />
            </p>
          }
          name="is_reservable"
          rules={[{ required: true, message: $t("Selecteer waarde") }]}
        >
          <SwitchYesNo disabled={readOnly} />
        </Form.Item>

        <Form.Item
          label={
            <p>
              {$t("Heeft schadekaart")}{" "}
              <HelpBubble
                content={$t(
                  "Activeert schadekaart om meldingen visueel aan te geven.",
                )}
              />
            </p>
          }
          name="enable_damage_schematic"
          rules={[{ required: true, message: $t("Selecteer waarde") }]}
        >
          <SwitchYesNo disabled={readOnly} />
        </Form.Item>

        {enable_damage_schematic && (
          <Form.Item
            label={
              <p>
                {$t("Schadekaart")}{" "}
                <HelpBubble
                  content={$t(
                    "Afbeelding waarop schades aangegeven worden. Vliegtuigen en voertuigen hebben een standaard schets, voor overig materiaal staat de schadekaart alleen aan met een eigen afbeelding.",
                  )}
                />
              </p>
            }
          >
            {!readOnly && (
              <>
                
                {schematicUrl && (
                  <img
                    src={schematicUrl}
                    alt={$t("Schadekaart")}
                    style={{
                      display: "block",
                      maxWidth: "100%",
                      maxHeight: 120,
                    }}
                  />
                )}
                <Form.Item
                  name="damage_schematic_upload"
                  valuePropName="fileList"
                  getValueFromEvent={normFileDuringFormUpload}
                  rules={[ruleCheckImageMaxSize]}
                  style={{ marginTop: 6, marginBottom: 0 }}
                >
                  <Upload
                    name="file"
                    listType="picture"
                    accept="image/*"
                    maxCount={1}
                    beforeUpload={() => false}
                  >
                    <Button size="small" icon={<UploadOutlined />}>
                      {$t("Upload eigen afbeelding")}
                    </Button>
                  </Upload>
                </Form.Item>
                {currentMaterial.damage_schematic &&
                  !removeSchematic &&
                  !schematicUpload?.length && (
                    <Button
                      size="small"
                      danger
                      style={{ marginTop: 6 }}
                      onClick={() => setRemoveSchematic(true)}
                      icon={<DeleteOutlined/>}
                    >
                      {$t("Verwijder afbeelding")}
                    </Button>
                  )}
              </>
            )}
          </Form.Item>
        )}

        {!readOnly && (
          <Form.Item
            label={
              <p>
                {$t("Opmerking")}{" "}
                <HelpBubble
                  content={$t("Bericht zichtbaar voor alle leden.")}
                />
              </p>
            }
            name="remark"
            rules={[
              {
                required: false,
                max: 700,
                message: $t("Maximaal ${c} characters", { c: 700 }),
              },
            ]}
          >
            <Input.TextArea rows={3} disabled={readOnly} />
          </Form.Item>
        )}

        {!readOnly && (
          <Form.Item
            label={
              <p>
                {$t("QR code")}{" "}
                <HelpBubble
                  content={$t(
                    "De QR code is een unieke code waarmee je direct de actuele status van het materiaal kan inzien. Ook vliegers die geen toegang hebben tot de app kunnen hiermee de status van de kist inspecteren voordat ze ermee gaan vliegen.",
                  )}
                />
              </p>
            }
          >
            <Button
              icon={<QrcodeOutlined />}
              size="small"
              style={{ marginRight: 12 }}
              loading={downloading}
              onClick={downloadCode}
            >
              {$t("Download")}
            </Button>
            <div className="small row" style={{ gap: 6, marginTop: 6 }}>
              {$t("Kopieer")} link:{" "}
              <CopyIcon
                text={`https://${window.location.host}/status/material/${currentMaterial.uuid}`}
              />
            </div>
          </Form.Item>
        )}
      </Form>
    </DataCard>
  );
};
